import { useTranslation } from "react-i18next";
import {
  AppIcon,
  Badge,
  ChartIcon,
  InfrastructureIcon,
  LockIcon,
  Menu,
  MenuItem,
  PhoneIcon,
  SettingIcon,
  SubMenu,
  TextIcon,
  ToolIcon,
  UserIcon,
} from "melco-ui";

import { useMainMenu, useRequirePermissions } from "melco-shared-logic";
import styled from "styled-components";
import { useSupportStatus } from "../../hooks/support/useSupportStatus";

const MenuItemWithOverflow = styled(MenuItem)`
  overflow: visible !important;

  & .ant-menu-title-content {
    overflow: visible !important;
  }
`;

const MenuItemWithBadgeWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MainMenu: React.FC = () => {
  const { t } = useTranslation();
  const { selectedKeys, onClick } = useMainMenu();

  const isAccessToInfrastructureAllowed = useRequirePermissions([
    "INFRASTRUCTURE",

  ]);

  const isAccessToManageMachineLicensesAllowed = useRequirePermissions([
    "MANAGE_MACHINE_LICENSES"
  ]);

  const isAccessToManageLicensesAllowed = useRequirePermissions([
    "MANAGE_LICENSES",
  ]);

  const isAccessToUserAdministrationAllowed = useRequirePermissions([
    "ADMINISTRATION",
    "MANAGE_USERS",
  ]);

  const isAccessToBillingAllowed = useRequirePermissions(["BILLING"]);

  const isAccessToPurchaseHistoryAllowed = useRequirePermissions([
    "PURCHASE_HISTORY",
  ]);

  const isAccessToSupportAllowed = useRequirePermissions(["GRANT_ACCESS"]);

  const isAccessToAPIKeysAllowed = useRequirePermissions(["API_KEYS"]);

  const isAccessToEODSettingsAllowed = useRequirePermissions(["EOD_SETTINGS"]);

  const isAccessToMachineReportingAllowed = useRequirePermissions([
    "MACHINE_REPORTING",
  ]);

  const { data: supportStatus } = useSupportStatus();
  const currentUrl = window.location.href;
  return (
    <Menu
      mode="inline"
      selectedKeys={selectedKeys}
      defaultOpenKeys={selectedKeys}
      onClick={onClick}
    >
      <MenuItem key="/dashboard" icon={<AppIcon />}>
        {t("main_menu.dashboard")}
      </MenuItem>

      {(currentUrl.includes("test-account") ||
        currentUrl.includes("dev-account")) && (
          <SubMenu
            key="/reporting"
            icon={<ChartIcon />}
            data-cy-id="reporting-menu-item"
            title={t("main_menu.reporting")}
          >
            <MenuItem
              data-cy-id="reporting-statistics-menu-item"
              key="/reporting/statistics"
            >
              {t("main_menu.reporting_statistics")}
            </MenuItem>
            <MenuItem
              data-cy-id="reporting-machine-status-menu-item" // icon={isAccessToMachineReportingAllowed ? "" : <LockIcon />}
              key="/reporting/machine/status"
            >
              {t("main_menu.reporting_machine_status")}
              {isAccessToMachineReportingAllowed ? (
                ""
              ) : (
                <>
                  {" "}
                  <LockIcon />
                </>
              )}
            </MenuItem>
            <MenuItem
              data-cy-id="reporting-machine-groups-menu-item"
              //icon={isAccessToMachineReportingAllowed ? <LockIcon /> : ""}
              key="/reporting/machine/groups"
            >
              {t("main_menu.reporting_machine_groups")}
              {isAccessToMachineReportingAllowed ? (
                ""
              ) : (
                <>
                  {" "}
                  <LockIcon />
                </>
              )}
            </MenuItem>
          </SubMenu>
        )}

      {isAccessToInfrastructureAllowed && (
        <SubMenu
          key="/infrastructure"
          icon={<InfrastructureIcon />}
          title={t("main_menu.infrastructure")}
        > {
            isAccessToManageLicensesAllowed &&
            (<MenuItem key="/infrastructure/licenses">
              {t("main_menu.infrastructure_licenses")}
            </MenuItem>)
          }
          {
            isAccessToManageMachineLicensesAllowed && (
              <MenuItem key="/infrastructure/machinelicenses">
                {t("main_menu.machine_licenses")}
              </MenuItem>
            )}

        </SubMenu>
      )}

      {isAccessToUserAdministrationAllowed && (
        <SubMenu
          key="/libraries"
          icon={<ToolIcon />}
          title={t("main_menu.administration")}
        >
          <MenuItem key="/administration/users">
            {t("main_menu.administration_users")}
          </MenuItem>
        </SubMenu>
      )}

      {isAccessToBillingAllowed && (
        <SubMenu
          key="/billing"
          // ⚠️ PLACEHOLDER ICON: reusing AppIcon here because it's the only
          // icon already confirmed to work in this app (see the imports
          // above) — none of the icons used elsewhere in this menu are a
          // semantic fit for "Billing". melco-ui may well export something
          // more fitting (a credit-card/invoice icon), but nothing in this
          // codebase uses one yet, so its exact name couldn't be confirmed
          // from here. Worth swapping for a proper billing icon once
          // that's checked against melco-ui's actual export list.
          icon={<TextIcon />}
          title={t("main_menu.billing")}
        >
          {isAccessToPurchaseHistoryAllowed && (
            <MenuItem key="/billing/purchases">
              {t("main_menu.billing_purchases")}
            </MenuItem>
          )}
        </SubMenu>
      )}

      <SubMenu
        key="/settings"
        icon={<SettingIcon />}
        title={t("main_menu.settings")}
      >
        <MenuItem key="/settings/basic">
          {t("main_menu.settings_basic")}
        </MenuItem>

        {isAccessToAPIKeysAllowed && (
          <MenuItem key="/settings/api">{t("main_menu.settings_api")}</MenuItem>
        )}
        {isAccessToEODSettingsAllowed && <MenuItem key="/settings/eod">
          {t("main_menu.settings_eod")}
        </MenuItem>}
      </SubMenu>

      <MenuItem key="/account/profile" icon={<UserIcon />}>
        {t("main_menu.profile")}
      </MenuItem>

      <MenuItem key="/account/login" icon={<LockIcon />}>
        {t("main_menu.login")}
      </MenuItem>

      {isAccessToSupportAllowed && (
        <MenuItemWithOverflow key="/account/support" icon={<PhoneIcon />}>
          <MenuItemWithBadgeWrapper>
            {t("main_menu.support")}

            {supportStatus?.isActive && (
              <Badge status="processing" data-cy-id="support-status-badge" />
            )}
          </MenuItemWithBadgeWrapper>
        </MenuItemWithOverflow>
      )}
    </Menu>
  );
};

export default MainMenu;
